import { inject, observer } from 'mobx-react';
import React, { useEffect, useRef, useState } from 'react';
import { ModuleContainer, ModuleDescription, ModuleIndex, ModuleTitle } from '../parts';
import PathIcon from '../PathIcon';
import ProgressEnum from '../../../../../models/learning/ProgressEnum';
import { PrimaryButton, AwaitingPill, SuccessPill } from '../projectButtons';
import ActionButton from '../Study/ActionButton';

const ProjectSubmission = ({ learningId, pathId, icon, module, disabled, learningStore }) => {
  const [assignment, setAssignment] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const fileRef = useRef(null);
  const active = module.progress === ProgressEnum.InProgress || module.progress === ProgressEnum.Completed;

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const a = await learningStore.fetchProjectAssignmentStatus(learningId);
        if (!cancelled) setAssignment(a);
      } catch (e) {
        if (!cancelled) setErr(e.message);
      }
    }
    load();

    // The post-workshop survey submits in its own tab, so this card must re-read on focus to
    // notice that the certificate has unlocked.
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
    };
  }, [learningId, learningStore]);

  const onSubmitClick = () => fileRef.current && fileRef.current.click();

  const onFile = async e => {
    // Capture the input node now: React 16 pools synthetic events, so `e.target` is null by the
    // time the await below settles (see the same note in PreAssessment).
    const input = e.target;
    const file = input.files && input.files[0];
    if (!file) return;
    setErr(null);
    setBusy(true);
    try {
      const { assignment: updated } = await learningStore.uploadFinalSubmission(
        learningId,
        pathId,
        module.id,
        file,
      );
      setAssignment(updated);
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setBusy(false);
      input.value = '';
    }
  };

  const submissions = (assignment && assignment.submissions) || [];
  const approvalStatus = (assignment && assignment.approvalStatus) || 'PENDING';
  const awaitingReview = approvalStatus === 'PENDING' && submissions.length > 0;
  const banner =
    approvalStatus === 'APPROVED'
      ? { text: '✅ Approved — see Certificate of Achievement', color: '#2e7d32' }
      : approvalStatus === 'REJECTED'
      ? { text: '❌ Rejected — please address feedback and resubmit', color: '#c0392b' }
      : submissions.length === 0
      ? { text: 'No submission yet', color: '#5c6573' }
      : { text: `⏳ Awaiting instructor review (attempt #${submissions.length})`, color: '#b8860b' };
  const submitBlocked = approvalStatus === 'APPROVED' || awaitingReview;
  // Absent flag never unlocks the certificate; the server enforces the same condition.
  const postSurveyCompleted = !!(assignment && assignment.postSurveyCompleted);

  return (
    <ModuleContainer className="p3">
      <div className="flex justify-between">
        <ModuleIndex className={`${active ? 'active' : ''}`}>
          {(module.index + 1).toString().padStart(2, '0')}
        </ModuleIndex>
        <PathIcon icon={icon} active={active} />
      </div>
      <div>
        <ModuleTitle>{module.name || 'Project Submission'}</ModuleTitle>
        <ModuleDescription>{module.description}</ModuleDescription>
      </div>
      {assignment && (
        <p className="my2" style={{ fontSize: 14, color: '#5c6573' }}>
          Project: <strong>project{assignment.assignedProjectIndex}.zip</strong>
        </p>
      )}
      <p className="my2" style={{ fontSize: 14, color: banner.color, fontWeight: 600 }}>
        {banner.text}
      </p>
      {submissions.length > 0 && (
        <ul style={{ fontSize: 12, color: '#5c6573', paddingLeft: 16, marginBottom: 12 }}>
          {submissions
            .slice()
            .reverse()
            .map(s => (
              <li key={s.attempt}>
                Attempt #{s.attempt} — {new Date(s.submittedAt).toLocaleString()}
              </li>
            ))}
        </ul>
      )}
      <input
        ref={fileRef}
        type="file"
        accept=".zip,application/zip"
        style={{ display: 'none' }}
        onChange={onFile}
      />
      <div style={{ marginTop: 8 }}>
        {approvalStatus === 'APPROVED' ? (
          <SuccessPill>
            <span role="img" aria-label="locked">
              🔒
            </span>
            Submission Locked (Approved)
          </SuccessPill>
        ) : awaitingReview ? (
          <AwaitingPill>
            <span role="img" aria-label="awaiting">
              ⏳
            </span>
            Awaiting Instructor Review
          </AwaitingPill>
        ) : (
          <PrimaryButton type="button" onClick={onSubmitClick} disabled={disabled || busy}>
            <span role="img" aria-label="upload">
              ⬆
            </span>
            {busy
              ? 'Uploading…'
              : submissions.length === 0
              ? 'Submit Your Project'
              : 'Submit Another Version'}
          </PrimaryButton>
        )}
      </div>
      {module.workspaceStudyId && (
        <div className="custom-actions" style={{ marginTop: 12 }}>
          <ActionButton
            studyId={module.workspaceStudyId}
            enableWorkspace
            workspace={module.workspace || {}}
            disabled={disabled || module.workspaceLocked}
          />
        </div>
      )}

      {approvalStatus === 'APPROVED' && (
        <div
          style={{
            marginTop: 16,
            padding: 20,
            border: '2px solid #8c1515',
            borderRadius: 8,
            background: 'linear-gradient(135deg, #fffaf1 0%, #fff 100%)',
            textAlign: 'center',
            boxShadow: '0 4px 12px rgba(140, 21, 21, 0.08)',
          }}
        >
          <div style={{ fontSize: 11, letterSpacing: 1.5, color: '#8c1515', fontWeight: 700, marginBottom: 4 }}>
            STANFORD MEDICINE
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#2e2d29', marginBottom: 6 }}>
            🎓 Certificate of Achievement
          </div>
          <div style={{ fontSize: 13, color: '#5c6573', marginBottom: 14 }}>
            Your final project (<strong>project{assignment && assignment.assignedProjectIndex}.zip</strong>) has been
            approved
            {assignment && assignment.approvedAt ? ` on ${new Date(assignment.approvedAt).toLocaleDateString()}` : ''}.
          </div>
          <button
            type="button"
            className={`primary-black ${busy || !postSurveyCompleted ? 'disabled' : ''}`}
            disabled={busy || !postSurveyCompleted}
            onClick={async () => {
              setErr(null);
              setBusy(true);
              try {
                const { url } = await learningStore.issueAchievementCertificate(learningId);
                window.open(url, '_blank');
              } catch (ex) {
                setErr(ex.message);
              } finally {
                setBusy(false);
              }
            }}
          >
            <span role="img" aria-label="cert">
              🎓
            </span>{' '}
            Download Certificate
          </button>
          {!postSurveyCompleted && (
            <div style={{ fontSize: 13, color: '#b8860b', marginTop: 10 }}>
              <span role="img" aria-label="locked">
                🔒
              </span>{' '}
              Complete the Post-Workshop Survey (next card) to unlock your certificate.
            </div>
          )}
        </div>
      )}

      {err && (
        <p className="my2" style={{ fontSize: 12, color: '#c0392b' }}>
          {err}
        </p>
      )}
    </ModuleContainer>
  );
};

export default inject('learningStore')(observer(ProjectSubmission));
